import type {
  AcceptedBotDefeatOutcome,
  BotDefeatRecord,
  BotDefeatRuntime,
} from "./bot-defeat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { ProgressState } from "./progress-state";
import type {
  RoundSettlementQueueStatus,
  RoundSettlementReceipt,
  RoundSettlementStatus,
} from "./round-settlement";
import type { ProgressMutation } from "./progress-mutation";

export type FreePlaySettlementStatus =
  "conflict" | "duplicate" | "failed" | "idle" | "pending" | "settled";

export interface FreePlayRuntimeSnapshot {
  readonly coinsEarned: number;
  readonly pendingReceiptCount: number;
  readonly settledRewardCount: number;
  readonly settlementStatus: FreePlaySettlementStatus;
}

interface FreePlaySettlementStore {
  getState(): Pick<
    ProgressState,
    "pendingRoundSettlementReceipts" | "totalCoins"
  >;
  queueRound(
    receipt: RoundSettlementReceipt,
  ): ProgressMutation<RoundSettlementQueueStatus | "failed">;
  settleRound(
    receipt: RoundSettlementReceipt,
  ): ProgressMutation<RoundSettlementStatus | "failed">;
}

type FreePlayBotDefeatPort = Pick<
  BotDefeatRuntime,
  "acknowledgeReward" | "subscribe"
>;
interface FreePlayPlayerPort {
  getSnapshot(): { readonly player: { readonly level: number } | null };
}

interface PendingReceipt {
  readonly receipt: RoundSettlementReceipt;
  readonly reward: BotDefeatRecord | null;
}

const addCounter = (current: number, increment: number): number =>
  Math.min(Number.MAX_SAFE_INTEGER, current + increment);

export class FreePlayRuntime {
  private coinsEarned = 0;
  private destroyed = false;
  private readonly memoryPending = new Map<string, PendingReceipt>();
  private receiptSequence = 0;
  private sessionId: string | null = null;
  private settlementPending = false;
  private settledRewardCount = 0;
  private settlementStatus: FreePlaySettlementStatus = "idle";
  private readonly unsubscribeDefeats: () => void;
  private readonly unsubscribeFlow: () => void;

  public constructor(
    private readonly flow: GameFlow,
    private readonly botDefeat: FreePlayBotDefeatPort,
    private readonly player: FreePlayPlayerPort,
    private readonly settlement: FreePlaySettlementStore,
    private readonly createSessionId: () => string,
  ) {
    this.unsubscribeDefeats = botDefeat.subscribe((outcome) => {
      this.consume(outcome);
    });
    this.unsubscribeFlow = flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
    this.flushPending();
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.unsubscribeDefeats();
    this.unsubscribeFlow();
    this.memoryPending.clear();
  }

  public getSnapshot(): FreePlayRuntimeSnapshot {
    return {
      coinsEarned: this.coinsEarned,
      pendingReceiptCount: this.collectPending().size,
      settledRewardCount: this.settledRewardCount,
      settlementStatus: this.settlementStatus,
    };
  }

  public prepareForExit(): boolean {
    const snapshot = this.flow.getSnapshot();
    if (snapshot.scene !== "gameplay" || snapshot.mode !== "free-play") {
      return true;
    }
    return this.flushPending();
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "gameplay" && snapshot.mode === "free-play") {
      this.coinsEarned = 0;
      this.receiptSequence = 0;
      this.sessionId = this.createSessionId();
      this.settledRewardCount = 0;
      this.settlementStatus = "idle";
      this.flushPending();
      return;
    }
    this.coinsEarned = 0;
    this.receiptSequence = 0;
    this.sessionId = null;
    this.settledRewardCount = 0;
    this.settlementStatus = "idle";
  }

  private collectPending(): Map<string, PendingReceipt> {
    const pending = new Map<string, PendingReceipt>();
    for (const receipt of this.settlement.getState()
      .pendingRoundSettlementReceipts) {
      pending.set(receipt.id, { receipt, reward: null });
    }
    for (const [id, receipt] of this.memoryPending) {
      pending.set(id, receipt);
    }
    return pending;
  }

  private complete(pending: PendingReceipt): void {
    if (pending.reward !== null) {
      this.botDefeat.acknowledgeReward(pending.reward);
    }
    this.memoryPending.delete(pending.receipt.id);
    this.settledRewardCount = addCounter(this.settledRewardCount, 1);
  }

  private consume(outcome: AcceptedBotDefeatOutcome): void {
    const flow = this.flow.getSnapshot();
    const player = this.player.getSnapshot().player;
    if (
      flow.scene !== "gameplay" ||
      flow.mode !== "free-play" ||
      outcome.winnerId !== "player" ||
      outcome.rewardCoins <= 0 ||
      player === null ||
      this.sessionId === null
    ) {
      return;
    }
    this.receiptSequence += 1;
    const receipt: RoundSettlementReceipt = {
      coinsEarned: outcome.rewardCoins,
      finalLevel: player.level,
      foodCollected: 0,
      id: `free-play:${this.sessionId}:${String(outcome.runId)}:${String(this.receiptSequence)}`,
      mode: "free-play",
      result: null,
      snakesDefeated: 1,
    };
    this.coinsEarned = addCounter(this.coinsEarned, outcome.rewardCoins);
    this.memoryPending.set(receipt.id, {
      receipt,
      reward: outcome,
    });
    this.settlementStatus = "pending";
    this.flushPending();
  }

  private flushPending(): boolean {
    if (this.settlementPending) {
      return false;
    }
    const pendingReceipts = this.collectPending();
    if (pendingReceipts.size === 0) {
      return true;
    }
    for (const pending of pendingReceipts.values()) {
      const queued = this.settlement.queueRound(pending.receipt);
      if (queued instanceof Promise) {
        this.waitForQueue(pending, queued);
        return false;
      }
      if (!this.finishQueue(pending, queued)) {
        return false;
      }
    }
    return true;
  }

  private finishQueue(
    pending: PendingReceipt,
    queued: RoundSettlementQueueStatus | "failed",
  ): boolean {
    if (queued === "conflict" || queued === "failed") {
      this.settlementStatus = queued;
      return false;
    }
    if (queued === "duplicate") {
      this.complete(pending);
      this.settlementStatus = "duplicate";
      return true;
    }
    const settled = this.settlement.settleRound(pending.receipt);
    if (settled instanceof Promise) {
      this.waitForSettlement(pending, settled);
      return false;
    }
    return this.finishSettlement(pending, settled);
  }

  private finishSettlement(
    pending: PendingReceipt,
    settled: RoundSettlementStatus | "failed",
  ): boolean {
    if (settled === "conflict" || settled === "failed") {
      this.settlementStatus = settled;
      return false;
    }
    this.complete(pending);
    this.settlementStatus = settled;
    return true;
  }

  private waitForQueue(
    pending: PendingReceipt,
    queued: Promise<RoundSettlementQueueStatus | "failed">,
  ): void {
    this.settlementPending = true;
    this.settlementStatus = "pending";
    void queued.then(
      (status) => {
        this.settlementPending = false;
        if (this.finishQueue(pending, status)) {
          this.flushPending();
        }
      },
      () => {
        this.settlementPending = false;
        this.settlementStatus = "failed";
      },
    );
  }

  private waitForSettlement(
    pending: PendingReceipt,
    settled: Promise<RoundSettlementStatus | "failed">,
  ): void {
    this.settlementPending = true;
    this.settlementStatus = "pending";
    void settled.then(
      (status) => {
        this.settlementPending = false;
        if (this.finishSettlement(pending, status)) {
          this.flushPending();
        }
      },
      () => {
        this.settlementPending = false;
        this.settlementStatus = "failed";
      },
    );
  }
}
